'use client';

import { useEffect, useState, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Progress } from '@/shared/ui/shadcn/progress';
import { LoadingAnimation } from '@/shared/ui';
import { RenderStatus } from '@/shared/constants/status';
import { calculateTimeBasedProgress } from '@/shared/lib/render-status';
import { useConceptAlbumSettings } from '../model/album-settings.store';
import {
  buildAlbumStepKeys,
  activeAlbumStepIndex,
} from '../model/album-generation-steps';
import type { Job } from '@/features/design/job-queue';

interface AlbumProgressCardProps {
  album: Job;
  projectId?: string;
}

/**
 * Large progress card for album generation.
 *
 * Album rendering runs for several minutes, so the card narrates what the
 * pipeline is doing rather than showing a bare spinner. The step sequence is
 * built from the sections the user opted into (see album-generation-steps), and
 * the active step is derived from the time-based progress — no fake per-step
 * completion is claimed; the final "rendering" step holds until the album lands.
 */
export function AlbumProgressCard({
  album,
  projectId: _projectId,
}: AlbumProgressCardProps) {
  const t = useTranslations('album');
  const isQueued = album.status === RenderStatus.PENDING;
  const isProcessing = album.status === RenderStatus.PROCESSING;

  // Section flags come from the per-concept album settings — the same source
  // that built the generate request. Estimate has no persisted toggle (it is
  // included by default via the picker), so its cost steps always show.
  const settings = useConceptAlbumSettings(album.conceptId);
  const stepKeys = useMemo(
    () =>
      buildAlbumStepKeys({
        includeMaterials: settings.includeMaterials,
        includeEstimate: true,
        includeBlueprints: settings.includeBlueprints,
        includePreparationGuide: settings.includePreparationGuide,
        includeSafetyRequirements: settings.includeSafetyRequirements,
        includeBuildingRegulations: settings.includeBuildingRegulations,
        hasSpecialRequirements: settings.requirements.trim().length > 0,
      }),
    [
      settings.includeMaterials,
      settings.includeBlueprints,
      settings.includePreparationGuide,
      settings.includeSafetyRequirements,
      settings.includeBuildingRegulations,
      settings.requirements,
    ]
  );

  // Progress tick for smooth updates
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!isProcessing || !album.startedAt) return;
    const interval = setInterval(() => setTick((t) => t + 1), 500);
    return () => clearInterval(interval);
  }, [isProcessing, album.startedAt]);

  const progress = useMemo(() => {
    if (!isProcessing || !album.startedAt) return 0;
    return calculateTimeBasedProgress(
      album.startedAt,
      album.durationPredicted,
      album.id
    );
  }, [isProcessing, album.startedAt, album.durationPredicted, album.id, tick]);

  const isStalling = isProcessing && progress >= 95;

  const activeIndex = activeAlbumStepIndex(progress, stepKeys.length);
  const activeStepKey = stepKeys[activeIndex];

  return (
    <div className="flex justify-center w-full">
      <div className="w-full md:max-w-2xl bg-card border border-border shadow-sm rounded-xl flex flex-col justify-center gap-3 p-5 relative overflow-hidden [container-type:inline-size]">
        {isQueued ? (
          <div className="flex items-center gap-2">
            <LoadingAnimation type="dots" size="sm" />
            <span className="text-list-item-value text-muted-foreground">
              {t('generatingTitle')}
            </span>
          </div>
        ) : (
          <>
            {/* Narrated step — re-keyed so each change re-triggers the fade */}
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="text-card-meta uppercase tracking-wide text-primary">
                {t('generatingTitle')}
              </span>
              {activeStepKey && (
                <p
                  key={activeStepKey}
                  className="text-list-item-title text-foreground text-balance animate-fade-in"
                >
                  {t(`generatingSteps.${activeStepKey}`)}
                </p>
              )}
            </div>

            {/* Progress section */}
            <div className="w-full space-y-1.5">
              <Progress
                value={progress}
                className="h-1"
                isStalling={isStalling}
              />
              <div className="text-end">
                <span className="text-card-meta text-muted-foreground">
                  {Math.round(progress)}%
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
